import {getEnv} from '../../../../../../common/env/env';
import {fetchStrictly} from '../../../../../../common/shared/api/fetchStrictly';
import {ConvertedMedia} from '../type/ConvertedMedia.interface';
import {PreloadType} from '../type/PreloadType.enum';

const MAX_CONTENT_SIZE = 10 * 2 ** 20;

/**
 * 프리로드 된 이미지 정보를 반환합니다.
 */
export async function convertMedia(
  preloadType: PreloadType,
  url: string
): Promise<ConvertedMedia | undefined> {
  if (!url || !url.includes('/')) {
    return;
  }
  if (preloadType === PreloadType.Instance) {
    const img = new Image();
    img.src = url;
    return {
      src: url,
      instance: img,
    };
  }

  try {
    const headInfo = await fetchStrictly(url, {method: 'HEAD'});
    const contentSize = Number(headInfo.headers.get('Content-length'));
    const fileType = headInfo.headers.get('Content-type');
    // 충분한 헤더 정보가 없는 경우 또는 fileType이 비디오 타입인 경우 preload하지 않음
    // 비디오는 preload시 눈에 띄는 성능저하를 발생시키는 경우가 많아서 preload하지 않는다.
    if (!contentSize || !fileType || fileType.startsWith('video')) {
      return {
        src: url,
      };
    }
    // MAX_CONTENT_SIZE가 넘어가는 파일은 base64로 변환하지 않음. contentSize를 가져오지 못하는 경우(0인경우 포함)도 파일크기 초과로 간주한다.
    if (contentSize > MAX_CONTENT_SIZE) {
      return {
        dataUrl: undefined,
        src: url,
        isExceededFileSize: true,
      };
    }
  } catch (e) {
    console.error(
      'Failed to fetch HEAD in getConvertedMedia. url : %j',
      url,
      e
    );
    return;
  }
  try {
    const media = (await readAsDataUrl(url)) as string;
    return {
      dataUrl: media,
      src: url,
    };
  } catch (e) {
    console.error('Failed to preload. url : %j', url, e);
  }
  return;
}

async function readAsDataUrl(url: string) {
  let blob: Blob;
  try {
    blob = await fetchStrictly(url).then(r => r.blob());
  } catch (err) {
    const webStaticOrigin = getEnv().assetPrefix;
    const webOrigin = getEnv().webOrigin;
    // sandbox/real 환경에서 WEB_STATIC_HOST(static-page)에 fetch 요청을 보냈을 때 cors문제가 발생해서
    // 이 경우는 static-page를 통하지 않고 web host에서 리소스를 직접 가져갈 수 있도록 fallback처리를 해 주었다
    if (url.startsWith(webStaticOrigin)) {
      const regex = new RegExp(`^${webStaticOrigin}`);
      const directUrl = url.replace(regex, webOrigin);
      blob = await fetchStrictly(directUrl).then(r => r.blob());
    } else {
      throw err;
    }
  }
  const dataUrl = await new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
  return dataUrl;
}
